import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import { useAppConfig } from '../ConfigContext';

function formatMmSs(total) {
  const t = Math.max(0, Math.floor(Number(total) || 0));
  const mm = String(Math.floor(t / 60)).padStart(2, '0');
  const ss = String(t % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function formatHhMmSs(total) {
  const t = Math.max(0, Math.floor(Number(total) || 0));
  const hh = String(Math.floor(t / 3600)).padStart(2, '0');
  const mm = String(Math.floor((t % 3600) / 60)).padStart(2, '0');
  const ss = String(t % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

const normalizeText = (v) =>
  String(v || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

const pickPrizeIcon = (obj) => {

  const type = normalizeText(obj?.item?.type);
  const name = normalizeText(obj?.item?.name);
  const label = normalizeText(obj?.label);

  if (type.includes('SPIN') || name.includes('SPIN') || label.includes('SPIN')){return '/fortuneicon.png';}
  if (name.includes('CHEST') || label.includes('CHEST')){return '/chest.png';}
  if (label.includes('RING')) {return '/ring.png';}
  if (label.includes('SPARKS')){return '/sparks.png';}
  if (label.includes('ENERGY')){return '/energy.png';}
  if (label.includes('FLASH')){return '/evolveicon.png';}
  if (label.includes('RUNES')){return '/runes.png';}
  if (label.includes('FRAGMENT')){return '/fragment.png';}

  if (type.includes('RUNE') || name.includes('RUNE')) return '/runes.png';
  if (type.includes('SPARK') || name.includes('SPARK')) return '/sparks.png';
  if (type.includes('CARD_PIECE') || name.includes('PIECE')) return '/pieces.png';
  if (type.includes('EVOLVE') || name.includes('EVOLVE')) return '/evolveicon.png';
  if (type.includes('BOOST') || name.includes('ENERGY')) return '/energy.png';
  if (type.includes('ABILITY')) return '/fightlog.png';
  if (type.includes('SEASONAL_ACCESSORY')) return '/fragment.png';
  if (type.includes('FRAGMENT')) return '/fragment.png';
  if (type.includes('CARD')) return '/pack.png';
  if (type.includes('CHEST')) return '/chest.png';
  if (type.includes('PACK')) return '/pack.png';
  if (type.includes('RING')) return '/ring.png';

  return '/runes.png';
};

const rewardToModalItem = (reward, fallback = {}) => {
  const currency = String(reward?.currency || '').toLowerCase();

  if (currency === 'sparks') {
    return {
      id: reward?.id || `sparks-${Date.now()}`,
      label: reward?.label || 'Sparks Prize',
      icon: '/sparks.png',
      quantity: Number(reward?.sparks ?? reward?.amount ?? 0) || 0,
    };
  }

  if (currency === 'runes') {
    return {
      id: reward?.id || `runes-${Date.now()}`,
      label: reward?.label || 'Runes Prize',
      icon: '/runes.png',
      quantity: Number(reward?.runes ?? reward?.amount ?? 0) || 0,
    };
  }

  return {
    id: reward?.itemId || reward?.id || fallback.id || `item-${Date.now()}`,
    label: reward?.label || fallback.label || 'Reward',
    icon: fallback.icon || '/pack.png',
    quantity: Number(reward?.quantity ?? fallback.quantity ?? 1) || 1,
  };
};

export default function HiddenSeek({ onClose, onNavigate, user, setUser }) {
  const { loaded: cfgLoaded, hiddenSeek } = useAppConfig();

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [clicking, setClicking] = useState(false);
  const [state, setState] = useState(null);
  const [session, setSession] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [message, setMessage] = useState('');
  const imageWrapRef = useRef(null);
  const cooldownReloadedRef = useRef(false);
  const [showWinModal, setShowWinModal] = useState(false);
  const [winRewards, setWinRewards] = useState([]);
  const messageTimeoutRef = useRef(null);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTimeOverModal, setShowTimeOverModal] = useState(false);
  const token = localStorage.getItem('token');
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const sceneScrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [scrollHintDismissed, setScrollHintDismissed] = useState(false);

  const lastActiveSessionRef = useRef(null);
const [frozenGameSession, setFrozenGameSession] = useState(null);

const displaySession = showTimeOverModal ? (frozenGameSession || session) : session;

useEffect(() => {
  if (session) {
    lastActiveSessionRef.current = session;
  }
}, [session]);

const dismissScrollHint = () => {
  if (!scrollHintDismissed) {
    setScrollHintDismissed(true);
    setShowScrollHint(false);
  }
};

useEffect(() => {
  if (!displaySession || scrollHintDismissed) {
    setShowScrollHint(false);
    return;
  }

  setShowScrollHint(true);

  const timer = setTimeout(() => {
    setShowScrollHint(false);
  }, 4500);

  return () => clearTimeout(timer);
}, [displaySession?.id, scrollHintDismissed]);

  const goBackToArenas = () => {
  if (typeof onNavigate === 'function') {
    onNavigate('gamesHub');
    return;
  }

  onClose?.();
};

  const getHiddenSeekErrorMessage = (code) => {
  switch (code) {
    case 'ALL_SCENES_COMPLETED':
      return 'You completed all available scenes. New scenes coming soon.';
    case 'COOLDOWN_ACTIVE':
      return 'You need to wait before playing again.';
    case 'MAX_ROUNDS_REACHED':
      return 'You reached the daily limit.';
    case 'INSUFFICIENT_RUNES':
      return 'You do not have enough runes.';
    case 'HIDDEN_SEEK_DISABLED':
      return 'This event is disabled.';
    case 'NO_ACTIVE_SCENES':
      return 'There are no active scenes available.';
    default:
      return 'Something went wrong.';
  }
};

const showTemporaryMessage = (text, duration = 1500) => {
  setMessage(text);

  if (messageTimeoutRef.current) {
    clearTimeout(messageTimeoutRef.current);
  }

  messageTimeoutRef.current = setTimeout(() => {
    setMessage('');
  }, duration);
};

useEffect(() => {
  return () => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
  };
}, []);

  const api = async (path, options = {}) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const err = new Error(data?.error || `Request failed: ${res.status}`);
      err.error = data?.error || `Request failed: ${res.status}`;
      err.status = res.status;
      throw err;
    }

    return data;
  };

  const loadState = async () => {
    try {
      setLoading(true);
      const data = await api('/api/hidden-seek/state');
      setState(data);
      setSession(data?.session || null);

      if (data?.session?.expiresAt) {
        setSecondsLeft(
          Math.max(
            0,
            Math.floor((new Date(data.session.expiresAt).getTime() - Date.now()) / 1000)
          )
        );
      } else {
        setSecondsLeft(0);
      }

      if (data?.playState?.nextPlayAt) {
        setCooldownLeft(
          Math.max(
            0,
            Math.floor((new Date(data.playState.nextPlayAt).getTime() - Date.now()) / 1000)
          )
        );
      } else {
        setCooldownLeft(0);
      }
    } catch (e) {
      console.error('[HiddenSeek/loadState]', e);
      setMessage(e?.error || e?.message || 'Failed to load game');
      setState(null);
      setSession(null);
      setSecondsLeft(0);
      setCooldownLeft(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cfgLoaded) return;
    loadState();
  }, [cfgLoaded]);

  useEffect(() => {
    if (!session?.expiresAt) return;

    const timer = setInterval(() => {
      const next = Math.max(
        0,
        Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 1000)
      );
      setSecondsLeft(next);

     if (next <= 0) {
        clearInterval(timer);
        setSecondsLeft(0);
        setFrozenGameSession(lastActiveSessionRef.current || session);
        setShowTimeOverModal(true);
      }
    }, 250);

    return () => clearInterval(timer);
  }, [session?.expiresAt]);

useEffect(() => {
  if (displaySession) return;

  const nextPlayAt = state?.playState?.nextPlayAt;
  if (!nextPlayAt) {
    setCooldownLeft(0);
    return;
  }

  const updateCooldown = () => {
    const next = Math.max(
      0,
      Math.floor((new Date(nextPlayAt).getTime() - Date.now()) / 1000)
    );
    setCooldownLeft(next);
  };

  updateCooldown();

  const timer = setInterval(updateCooldown, 1000);
  return () => clearInterval(timer);
}, [state?.playState?.nextPlayAt, displaySession]);
  
  const startGame = async () => {
    try {
      setStarting(true);
      setMessage('');

      const data = await api('/api/hidden-seek/start', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (typeof setUser === 'function') {
        setUser((prev) => {
          const currentRunes = Number(prev?.runes || 0);
          const fee = Number(cfg.entryFeeRunes || 0);

          return {
            ...(prev || {}),
            runes: Math.max(0, currentRunes - fee),
          };
        });
      }

      setSession(data?.session || null);

      if (data?.session?.expiresAt) {
        setSecondsLeft(
          Math.max(
            0,
            Math.floor((new Date(data.session.expiresAt).getTime() - Date.now()) / 1000)
          )
        );
      } else {
        setSecondsLeft(0);
      }

      await loadState();
    } catch (e) {
      console.error('[HiddenSeek/startGame]', e);
      setErrorMessage(getHiddenSeekErrorMessage(e?.error || e?.message));
      setShowErrorModal(true);
      await loadState();
    } finally {
      setStarting(false);
    }
  };

  const handleImageClick = async (e) => {
    if (!session || clicking || secondsLeft <= 0 || !imageWrapRef.current || showWinModal || showTimeOverModal) return;

    try {
      setClicking(true);
      setMessage('');

      const rect = imageWrapRef.current.getBoundingClientRect();
      const x = Number((((e.clientX - rect.left) / rect.width) * 100).toFixed(2));
      const y = Number((((e.clientY - rect.top) / rect.height) * 100).toFixed(2));

      const data = await api('/api/hidden-seek/click', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: session.id,
          x,
          y,
        }),
      });

      if (data?.hit) {
        setSession((prev) => ({
          ...(prev || {}),
          foundObjectIds: data?.foundObjectIds || prev?.foundObjectIds || [],
        }));

       const responseRewards = Array.isArray(data?.rewards)
  ? data.rewards
  : data?.reward
    ? [data.reward]
    : [];

if (typeof setUser === 'function' && responseRewards.length) {
  setUser((prev) => {
    const next = { ...(prev || {}) };

    responseRewards.forEach((reward) => {
      if (reward.currency === 'runes') {
        next.runes = Number(next.runes || 0) + Number(reward.runes ?? reward.amount ?? 0);
      }

      if (reward.currency === 'sparks') {
        next.sparks = Number(next.sparks || 0) + Number(reward.sparks ?? reward.amount ?? 0);
      }
    });

    return next;
  });
}

        showTemporaryMessage(
  data?.label ? `Found: ${data.label}` : 'Found',
  1800
);

      } else {
        const missMessages = [
  'Nothing Here',
  'Keep Looking',
  'Not This Spot',
  'Cold Trail',
  'Almost',
];

showTemporaryMessage(
  missMessages[Math.floor(Math.random() * missMessages.length)],
  1400
);
      }

      if (data?.completed) {
  showTemporaryMessage('All Objects Found', 2000);

  const completedRewards = Array.isArray(data?.rewards) ? data.rewards : [];

  const itemFallbacksById = new Map(
    sessionObjects.map((obj) => [
      obj.rewardItemId,
      {
        id: obj.id,
        label: obj.item?.name || obj.label,
        icon: pickPrizeIcon(obj),
        quantity: obj.quantity || 1,
      },
    ])
  );

  setWinRewards(
    completedRewards.map((reward, index) => {
      const fallback = itemFallbacksById.get(reward?.itemId) || {};
      return rewardToModalItem(reward, {
        ...fallback,
        id: fallback.id || `reward-${index}`,
      });
    })
  );

  setTimeout(() => {
    setShowWinModal(true);
  }, 350);
}
    } catch (e) {
      console.error('[HiddenSeek/handleImageClick]', e);
      setMessage(e?.error || e?.message || 'Click failed');

      if (e?.error === 'SESSION_EXPIRED') {
        await loadState();
      }
    } finally {
      setClicking(false);
    }
  };

 const foundObjectIds = Array.isArray(displaySession?.foundObjectIds)
  ? displaySession.foundObjectIds
  : [];

const sessionObjects = Array.isArray(displaySession?.scene?.objects)
  ? displaySession.scene.objects
  : [];

  const remainingObjects = useMemo(() => {
    return sessionObjects.filter((obj) => !foundObjectIds.includes(obj.id));
  }, [sessionObjects, foundObjectIds]);

 const cfg = hiddenSeek || {
  enabled: false,
  entryFeeRunes: 0,
  cooldownSeconds: 86400,
  roundDurationSeconds: 60,
  maxRoundsPerDay: 1,

  rewardItems: false,
  sparksPrize: 0,
  runesPrize: 0,

  scenes: [],
};

  const playState = state?.playState || {};
  const canPlayByCooldownNow = playState?.canPlayByCooldown === false ? cooldownLeft <= 0 : true;
  const canPlayByLimitNow = playState?.canPlayByLimit !== false;
  const canPlay = canPlayByCooldownNow && canPlayByLimitNow;

  let startDisabledReason = '';

  if (starting) {
    startDisabledReason = 'Starting...';
  } else if (!canPlay) {
    if (!canPlayByCooldownNow && cooldownLeft > 0) {
      startDisabledReason = `Next Attempt in ${formatHhMmSs(cooldownLeft)}`;
    } else if (!canPlayByLimitNow) {
      startDisabledReason = 'Daily Limit Reached';
    } else {
      startDisabledReason = 'Cannot Play Right Now';
    }
  }

 useEffect(() => {
  if (!state?.playState?.nextPlayAt) {
    cooldownReloadedRef.current = false;
    return;
  }

  if (cooldownLeft > 0) {
    cooldownReloadedRef.current = false;
    return;
  }

  if (!cooldownReloadedRef.current) {
    cooldownReloadedRef.current = true;
    loadState();
  }
}, [cooldownLeft, state?.playState?.nextPlayAt]);

 if (!cfgLoaded || loading) {
  return (
    <div
      style={{
        padding: 0,
        color: "#fff",
        background: "url('/hidden.png') top / cover no-repeat",
        height: "calc(100dvh - 150px - var(--tg-safe-top))",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          minHeight: 430,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner
          text="Loading Hidden Seek"
          size={64}
        />
      </div>
    </div>
  );
}

  if (!cfg.enabled) {
    return (
      <div style={{ padding: 16, color: '#fff', textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>Hidden Seek</div>
        <div style={{ marginBottom: 16 }}>This event is disabled.</div>
        <button
          onClick={goBackToArenas}
          style={{
            background: '#6cbc57',
            color: '#fff',
            border: 'none',
            borderRadius: 14,
            padding: '12px 24px',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
  <div
    style={{
      padding: 0,
      color: '#fff',
      background: "url('/hidden.png') top / cover no-repeat",
      height: "calc(100dvh - 150px - var(--tg-safe-top))"
    }}
  >
    <style>
      {`
        @keyframes hiddenSeekScrollHintBounce {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(8px);
          }
        }
      `}
    </style>
      {!displaySession ? (
        <div style={{ textAlign: 'center', paddingTop: '42%' }}>
          <div className="shadow1"
            style={{
              margin: '0 auto 22px',
              maxWidth: "70%",
              background: 'rgba(0,0,0,0.5)',
              borderRadius: 18,
              padding: '16px 18px',
              border: "3px solid #FCB028",
              fontFamily: '"Fredoka", sans-serif',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 21}}>
              Duration: {cfg.roundDurationSeconds || 60}s
            </div>
            <div style={{ fontWeight: 600, fontSize: 21}}>
              Entry Fee: {cfg.entryFeeRunes || 0} <img src="/runes.png" style={{height: 28, verticalAlign: "middle"}}/> Runes
            </div>
            {(Number(cfg.sparksPrize || 0) > 0 || Number(cfg.runesPrize || 0) > 0 || cfg.rewardItems) && (
  <div style={{ marginTop: 8 }}>
    <div style={{ fontWeight: 600, fontSize: 21}}>
      Prize:
    </div>

    {Number(cfg.sparksPrize || 0) > 0 && (
      <div style={{ fontWeight: 600, fontSize: 21}}>
        {cfg.sparksPrize} <img src="/sparks.png" style={{height: 24, verticalAlign: "middle"}}/> Sparks
      </div>
    )}

    {Number(cfg.runesPrize || 0) > 0 && (
      <div style={{ fontWeight: 600, fontSize: 21}}>
        {cfg.runesPrize} <img src="/runes.png" style={{height: 24, verticalAlign: "middle"}}/> Runes
      </div>
    )}

    {cfg.rewardItems && (
      <div style={{ fontWeight: 600, fontSize: 21}}>
        + Found Items
      </div>
    )}
  </div>
)}

             {startDisabledReason ? (
            <div
              style={{
                marginTop: 6,
                color: '#fff',
                fontWeight: 600,
                fontSize: 21
              }}
            >
              {startDisabledReason}
            </div>
          ) : null}
          </div>

          {message ? (
            <div
              style={{
                marginBottom: 12,
                color: '#ffd27a',
                whiteSpace: 'pre-wrap',
                fontWeight: 600,
              }}
            >
              {message}
            </div>
          ) : null}

          <button
            onClick={() => setStartConfirmOpen(true)}
            disabled={starting || !canPlay}
            className="game-button green-button"
            style={{
              cursor: starting || !canPlay ? 'not-allowed' : 'pointer',
              opacity: starting || !canPlay ? 0.6 : 1,
              marginTop: 30,
              marginBottom: 20
            }}
          >
            {starting ? 'Starting…' : 'Start Game'}
          </button>
<br/>
          <button
          className="game-button-small red-button"
            onClick={() => setShowInstructionsModal(true)}
          >
            INSTRUCTIONS
          </button>

          <br/>

          <div
            style={{
              marginTop: 14,
              fontSize: 14,
              color: '#fff',
              textShadow: '1px 1px #000',
              background: 'rgba(0,0,0,0.28)',
              display: 'inline-block',
              padding: '10px 14px',
              borderRadius: 14,
              fontFamily: '"Fredoka", sans-serif',
            }}
          >
            Tap <span style={{ color: '#F8BA1E', fontWeight: 600 }}>Instructions</span> to see how to play.
          </div>

        </div>
      ) : (
  <div
    ref={sceneScrollRef}
    onScroll={dismissScrollHint}
    style={{
      height: "calc(100dvh - 120px - var(--tg-safe-top))",
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
      position: "relative",
    }}
  >
          <div
  ref={imageWrapRef}
  onClick={handleImageClick}
  onPointerDown={undefined}
  style={{
    position: 'relative',
    width: '100%',
    cursor: clicking ? 'wait' : 'crosshair',
    background: '#2a120d',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  }}
>

{showScrollHint && (
  <>
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: 90,
        pointerEvents: 'none',
        zIndex: 998,
        background:
          'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))',
      }}
    />

    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 145,
        transform: 'translateX(-50%)',
        zIndex: 999,
        pointerEvents: 'none',
        background: 'rgba(0,0,0,0.65)',
        color: '#fff',
        border: '2px solid #F8BA1E',
        borderRadius: 18,
        padding: '8px 14px',
        fontSize: 16,
        fontWeight: 900,
        textShadow: '1px 1px #000',
        boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
        animation: 'hiddenSeekScrollHintBounce 1.1s ease-in-out infinite',
        whiteSpace: 'nowrap',
        fontFamily: '"Fredoka", sans-serif',
      }}
    >
      ↕ Swipe to Explore the Full Scene
    </div>
  </>
)}

          <div
              style={{
                position: "fixed",
                top: "calc(var(--tg-safe-top) + 60px)",
                left: 0,
                textShadow: "1px 1px #000",
                backgroundColor: "rgba(0,0,0,0.3)",
                width: '100%',
                boxSizing: "border-box",
                borderBottom: "2px solid #000"
              }}
            >

             <div
             className="shadow2"
              style={{
                position: "relative",
                float: 'left',
                color: '#fff',
                borderRadius: 16,
                fontSize: 28,
                fontWeight: 'bold',
                height: 48,
                lineHeight: "48px",
                paddingLeft: 12,
                fontFamily: '"Fredoka", sans-serif',
              }}
            >
              {displaySession?.scene?.name || 'Scene'}
            </div>

            <div
              style={{
                position: "relative",
                float: 'right',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 900,
                fontSize: 24,
                backgroundColor: "rgba(0,0,0,0.3)",
                paddingLeft: 3,
                paddingRight: 10,
                borderRadius: 12,
                marginTop: 8
              }}
            >
              <span role="img" aria-label="timer"><img src="/v2/clock2.png" style={{marginRight: -5,height: 28,verticalAlign: "middle"}}/></span>
              <span style={{fontFamily: '"Fredoka", sans-serif',fontSize: 18,textShadow: "1px 1px #000"}}>{formatMmSs(secondsLeft)}</span>
            </div>

            </div>

            {displaySession?.scene?.imageUrl ? (
              <img
                src={displaySession.scene.imageUrl}
                alt={displaySession?.scene?.name || 'scene'}
                style={{ width: '100%', display: 'block'}}
              />
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>
                Scene image missing
              </div>
            )}
          </div>

          <div
            style={{
              position: "fixed",
              top: "calc(120px + var(--tg-safe-top))",
              right: "max(5px, env(safe-area-inset-right))",
              display: 'flex',
              justifyContent: 'center',
              gap: 5,
              flexWrap: 'wrap',
            }}
          >
           {sessionObjects.map((obj) => {
              const found = foundObjectIds.includes(obj.id);
              const resolvedLabel = obj.item?.name || obj.label;
              const resolvedIcon = pickPrizeIcon(obj);

              return (
                <div
                  key={obj.id}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: '#2b0906',
                    border: '1px solid #7a1d16',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: found ? 0.45 : 1,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  title={resolvedLabel}
                >
                  <img
                    src={resolvedIcon}
                    alt={resolvedLabel}
                    style={{
                      maxWidth: '70%',
                      maxHeight: '70%',
                      objectFit: 'contain',
                      filter: found ? 'grayscale(0.15)' : 'none',
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div
            style={{
              position: "fixed",
              bottom: 100,
              left: 0,
              width: '100%',
              textAlign: 'center',
              color: '#fff',
              fontSize: 21,
              whiteSpace: 'pre-wrap',
              fontFamily: '"Fredoka", sans-serif',
              fontWeight: 600
            }}
          >
            <span style={{borderRadius: 12, padding: 8, textShadow: "1px 1px #000",backgroundColor: "rgba(0,0,0,0.5)"}}>{message || (remainingObjects.length ? `Remaining: ${remainingObjects.length}` : 'Completed')}</span>
          </div>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              onClick={goBackToArenas}
              style={{
                position: "fixed",
                top: "calc(12px + var(--tg-safe-top))",
                right: "max(12px, env(safe-area-inset-right))",
                zIndex: 999,
                border: 'none',
                background: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img
                src="/v2/close.png"
                alt="Close"
                style={{
                  width: 36,
                  height: 36,
                  display: "block",
                  pointerEvents: "none", // important so the click hits the button
                }}
              />
            </button>
          </div>
        </div>
      )}

      {showWinModal && (
  <div className="modal_box"
  >

  <div className="modal_title">WooHoo!<button onClick={() => {
            setShowWinModal(false);
            goBackToArenas();
          }} className="modal_close"/></div>
    <div className="modal_body"
    >
    <div className="modal_inside">
      <div
      className="shadow2"
        style={{
          fontSize: 24,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        Scene Cleared
      </div>

      <div
      className="shadow1"
        style={{
          fontSize: 18,
          marginBottom: 20,
          fontWeight: 600
        }}
      >
        You Found All Hidden Objects
      </div>

      <div
      className="info"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
          gap: 12,
        }}
      >
        {winRewards.map((reward) => (
          <div
            key={reward.id}
          >
            <img
              src={reward.icon}
              alt={reward.label}
              style={{
                width: 42,
                height: 42,
                objectFit: 'contain',
                marginBottom: 8,
              }}
            />
            <div
  style={{
    fontSize: 21,
    fontWeight: 600,
    lineHeight: 1.2,
  }}
>
  {reward.label}
</div>

{Number(reward.quantity || 0) > 0 && (
  <div
    style={{
      marginTop: 4,
      fontSize: 21,
      fontWeight: 900,
      color: '#ffd27a',
      textShadow: '1px 1px #000',
    }}
  >
    {reward.quantity}
  </div>
)}
          </div>
        ))}
      </div></div>

      <div className="modal_footer">
        <button
          onClick={() => {
            setShowWinModal(false);
            setSession(null);
            loadState();
          }}
         className="game-button green-button" style={{marginRight: 10}}
        >
          Play Again
        </button>

        <button
          onClick={() => {
            setShowWinModal(false);
            goBackToArenas();
          }}
          className="game-button red-button"
        >
          CLOSE
        </button>
      </div>
    </div>
  </div>
)}


{startConfirmOpen && (
  <div className="modal_box">
  <div className="modal_title">Confirmation<button onClick={() => setStartConfirmOpen(false)} className="modal_close"/></div>
    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
    <div className="modal_inside">
      <img
        src="/finger.png"
        alt="Confirm"
        style={{ height: '80px', marginBottom: '10px' }}
      />

      <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: '10px' }}>
        Start Hidden Seek?
      </div>

      <p className="info"
        style={{
          fontSize: 18,
          color: '#fff',
        }}
      >
        Start a new game for { }
        <span style={{ color: '#F8BA1E' }}>
          {cfg.entryFeeRunes || 0} Runes
        </span>
        ?
      </p>

      </div>
      <div
        className="modal_footer"
      >
        <button
        className="game-button green-button" style={{marginRight: 10}}
          onClick={async () => {
            setStartConfirmOpen(false);
            await startGame();
          }}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Confirm'}
        </button>

        <button className="game-button red-button"
          onClick={() => setStartConfirmOpen(false)}
          disabled={starting}
        >
          CLOSE
        </button>
      </div>
    </motion.div>
  </div>
)}


{showErrorModal && (
  <div className="modal_box">
  <div className="modal_title">OOPS<button onClick={() => setShowErrorModal(false)} className="modal_close"/></div>
    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
    <div className="modal_inside">
      <img
        src="/finger.png"
        alt="Error"
        style={{ height: '80px', marginBottom: '10px', transform: 'rotate(180deg)' }}
      />

      <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: '10px' }}>
        Cannot Start
      </div>

      <p className="info"
        style={{
          fontSize: 18,
        }}
      >
        {errorMessage}
      </p>

      </div>

      <div
        className="modal_footer"
      >
        <button className="game-button red-button"
          onClick={() => setShowErrorModal(false)}
        >
          CLOSE
        </button>
      </div>
    </motion.div>
  </div>
)}



{showTimeOverModal && (
  <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
  <div className="modal_title">OOPS<button className="modal_close" onClick={() => {
            setShowTimeOverModal(false);
            setFrozenGameSession(null);
            goBackToArenas();
          }}/></div>
    <div className="modal_body">
    <div className="modal_inside">
      <img
        src="/v2/clock2.png"
        alt="Time Over"
        style={{
          height: '40px',
          marginBottom: '0px',
        }}
      />

      <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: '10px' }}>
        Time Over
      </div>

      <p className="info"
        style={{
          fontSize: 18,
        }}
      >
        You ran out of time.
        <br />
        Found {foundObjectIds.length} of {sessionObjects.length} objects.
      </p>

      </div>
      <div
        className="modal_footer"
      >
        <button
        className="game-button green-button" style={{marginRight: 10}}
          onClick={() => {
            setShowTimeOverModal(false);
            setFrozenGameSession(null);
            setSession(null);
            loadState();
          }}
        >
          Try Again
        </button>

        <button className="game-button red-button"
         onClick={() => {
            setShowTimeOverModal(false);
            setFrozenGameSession(null);
            goBackToArenas();
          }}
        >
          CLOSE
        </button>
      </div>
    </div>
  </motion.div>
)}

{showInstructionsModal && (
  <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
  <div className="modal_title">Instructions<button className="modal_close" onClick={() => setShowInstructionsModal(false)}/></div>
    <div className="modal_body">
    <div className="modal_inside">
      <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: '10px' }}>
        How To Play
      </div>

      <p className="info"
        style={{
          fontSize: 16,
          lineHeight: 1.2,
          textAlign: 'center',
          color: '#fff',
          marginBottom: 18,
          fontWeight: 'normal'
        }}
      >
        Find all hidden objects before the timer runs out. Tap the scene to discover them.
        Each round costs{' '}
        <span style={{ color: '#F8BA1E' }}>
          {cfg.entryFeeRunes || 0} Runes
        </span>
        . Complete the scene to collect the configured prizes.

      <img src="/inst.png" style={{width: "100%",marginTop: 15}}/>
            </p>

      </div>

      <div
        className="modal_footer"
      >
        <button className="game-button green-button"
          onClick={() => setShowInstructionsModal(false)}
          style={{ minWidth: 160 }}
        >
          Got It
        </button>
      </div>
    </div>
  </motion.div>
)}

    </div>
  );
}